if (!customElements.get('cart-free-shipping-progress')) {
  /** @type {number[]} */
  let sharedRefreshTimers = [];
  const ADDRESS_STORAGE_KEY = 'unilar-address';
  const HAS_CEP_HTML_CLASS = 'cart-drawer-has-cep';
  const HAS_CEP_DRAWER_CLASS = 'cart-drawer--has-cep';

  /**
   * @param {Storage} storage
   * @returns {object | null}
   */
  function readAddressFromStorage(storage) {
    try {
      const raw = storage.getItem(ADDRESS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * @returns {object | null}
   */
  function getSavedAddressShared() {
    try {
      const fromLocal = readAddressFromStorage(localStorage);
      const fromSession = readAddressFromStorage(sessionStorage);
      if (!fromLocal) return fromSession;
      if (!fromSession) return fromLocal;
      const localUpdatedAt = Number(fromLocal.updatedAt) || 0;
      const sessionUpdatedAt = Number(fromSession.updatedAt) || 0;
      return sessionUpdatedAt >= localUpdatedAt ? fromSession : fromLocal;
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {object | null} [address]
   * @returns {boolean}
   */
  function hasSavedCepShared(address = getSavedAddressShared()) {
    const cep = String(address?.cep || address?.zip || address?.postalCode || '').replace(/\D/g, '');
    return cep.length === 8;
  }

  /**
   * Sem CEP: trigger no topo. Com CEP: move para o summary (toggle de cupom).
   * Também marca classes no drawer/html para o CSS esconder o slot do topo no morph.
   * Nunca move o popup enquanto o <dialog> estiver aberto — isso tira o modal
   * da top layer e ele passa a renderizar “dentro” da drawer.
   */
  function relocateShippingRatesPopupShared() {
    const drawer = document.querySelector(
      'cart-drawer-component.cart-drawer, .cart-drawer, cart-drawer-component'
    );
    const hasCep = hasSavedCepShared();

    document.documentElement.classList.toggle(HAS_CEP_HTML_CLASS, hasCep);
    if (drawer) {
      drawer.classList.toggle(HAS_CEP_DRAWER_CLASS, hasCep);
    }

    if (!drawer) return;

    const popup = drawer.querySelector('.shipping-rates-popup--cart-drawer');
    const topSlot = drawer.querySelector('[data-cart-shipping-rates-slot="top"]');
    const summarySlot = drawer.querySelector('[data-cart-shipping-rates-slot="summary"]');
    if (!popup || !topSlot || !summarySlot) return;

    const dialog = popup.querySelector('dialog.shipping-rates__modal, dialog[ref="dialog"], dialog');
    const dialogOpen = Boolean(dialog?.open || dialog?.hasAttribute('open'));

    // Atualiza o label mesmo com o modal aberto; só adia o move do DOM.
    const label = popup.querySelector('.shipping-rates__trigger-text');
    if (label) {
      label.textContent = 'Consultar prazos e valores de entrega';
    }

    if (dialogOpen) {
      ensureRelocateOnDialogClose(dialog);
      return;
    }

    const targetSlot = hasCep ? summarySlot : topSlot;
    if (popup.parentElement !== targetSlot) {
      targetSlot.appendChild(popup);
    }
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  function ensureRelocateOnDialogClose(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.dataset.shippingRatesRelocateOnClose === 'true') return;
    dialog.dataset.shippingRatesRelocateOnClose = 'true';

    const onClose = () => {
      dialog.dataset.shippingRatesRelocateOnClose = 'false';
      dialog.removeEventListener('close', onClose);
      // Espera o close/animação terminar antes de mover o nó.
      queueMicrotask(() => {
        relocateShippingRatesPopupShared();
        requestAnimationFrame(relocateShippingRatesPopupShared);
      });
    };

    dialog.addEventListener('close', onClose);
  }

  /**
   * Agenda refresh no documento para sobreviver ao morph do cart-drawer
   * (que desconecta o custom element e cancelaria timers locais).
   * @param {number[]} delays
   */
  function scheduleSharedCartRefreshes(delays = [250, 800, 1400]) {
    // Relocate sync antes do paint pós-morph (o HTML Liquid volta o botão pro topo).
    relocateShippingRatesPopupShared();
    queueMicrotask(relocateShippingRatesPopupShared);
    requestAnimationFrame(relocateShippingRatesPopupShared);

    sharedRefreshTimers.forEach((timer) => window.clearTimeout(timer));
    sharedRefreshTimers = delays.map((delay) =>
      window.setTimeout(() => {
        relocateShippingRatesPopupShared();
        document.querySelectorAll('cart-free-shipping-progress').forEach((element) => {
          if (typeof element.refreshCartTotal === 'function') {
            element.refreshCartTotal();
          }
        });
      }, delay)
    );
  }

  // Observa o morph recolocando o popup no slot do topo e move de volta na hora.
  const shippingRatesMorphObserver = new MutationObserver(() => {
    if (!hasSavedCepShared()) return;
    const drawer = document.querySelector(
      'cart-drawer-component.cart-drawer, .cart-drawer, cart-drawer-component'
    );
    if (!drawer) return;
    const topSlot = drawer.querySelector('[data-cart-shipping-rates-slot="top"]');
    const popupInTop = topSlot?.querySelector('.shipping-rates-popup--cart-drawer');
    if (!popupInTop) return;

    const dialog = popupInTop.querySelector('dialog');
    if (dialog?.open || dialog?.hasAttribute('open')) return;

    relocateShippingRatesPopupShared();
  });

  function observeCartDrawerMorph() {
    const drawer = document.querySelector(
      'cart-drawer-component.cart-drawer, .cart-drawer, cart-drawer-component'
    );
    if (!drawer || drawer.dataset.shippingRatesMorphObserved === 'true') return;
    drawer.dataset.shippingRatesMorphObserved = 'true';
    shippingRatesMorphObserver.observe(drawer, { childList: true, subtree: true });
  }

  // Bootstrap: classe no <html> antes de qualquer ação no drawer.
  relocateShippingRatesPopupShared();
  observeCartDrawerMorph();
  document.addEventListener('cart:update', () => {
    relocateShippingRatesPopupShared();
    observeCartDrawerMorph();
  });
  document.addEventListener('cep:updated', () => {
    relocateShippingRatesPopupShared();
  });

  class CartFreeShippingProgress extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('[data-message]');
      this.barFill = this.querySelector('[data-bar-fill]');
      this.storageKey = 'unilar-cart-free-shipping-total-cents';
      this.addressStorageKey = ADDRESS_STORAGE_KEY;

      const attrTotal = this.readNumber(this.dataset.cartTotalCents);
      if (attrTotal > 0) {
        this.setCartTotal(attrTotal);
      } else {
        const stored = this.getStoredCartTotal();
        if (stored > 0) this.dataset.cartTotalCents = String(stored);
      }

      this.update();
      relocateShippingRatesPopupShared();
      observeCartDrawerMorph();
      // Reconcilia com o servidor após morph / re-mount do drawer.
      this.refreshCartTotal();

      this.handleCepUpdate = (event) => {
        if (event?.detail) {
          this.persistAddress(event.detail);
        }
        this.update();
        relocateShippingRatesPopupShared();
      };
      document.addEventListener('cep:updated', this.handleCepUpdate);
      document.addEventListener('cart:update', this.handleCartUpdate);
      document.addEventListener('discount:update', this.handleDiscountUpdate);
      document.addEventListener('quantity-selector:update', this.handleQuantitySelectorUpdate);
      document.addEventListener('click', this.handleCartRemoveClick, true);
      document.addEventListener('dialog:open', this.handleDrawerOpen, true);
    }

    disconnectedCallback() {
      document.removeEventListener('cep:updated', this.handleCepUpdate);
      document.removeEventListener('cart:update', this.handleCartUpdate);
      document.removeEventListener('discount:update', this.handleDiscountUpdate);
      document.removeEventListener('quantity-selector:update', this.handleQuantitySelectorUpdate);
      document.removeEventListener('click', this.handleCartRemoveClick, true);
      document.removeEventListener('dialog:open', this.handleDrawerOpen, true);
      // Não cancela sharedRefreshTimers: o morph recria o elemento e os timers
      // precisam atualizar a instância nova.
    }

    handleDrawerOpen = (event) => {
      const drawer = this.closest('cart-drawer-component');
      if (!drawer || event.target !== drawer) return;
      this.update();
      relocateShippingRatesPopupShared();
      this.refreshCartTotal();
    };

    handleCartUpdate = () => {
      this.shimmerMessage();
      // Não usa cart.total_price cru (sem PIX). Reconcilia com o total de vitrine.
      scheduleSharedCartRefreshes([150, 500, 1000]);
    };

    handleDiscountUpdate = () => {
      this.shimmerMessage();
      scheduleSharedCartRefreshes([150, 500, 1000]);
    };

    handleQuantitySelectorUpdate = (event) => {
      if (!event.target?.closest?.('.cart-drawer')) return;
      this.shimmerMessage();
      scheduleSharedCartRefreshes([250, 800, 1400]);
    };

    handleCartRemoveClick = (event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const nodes = path.length ? path : [event.target];

      const inDrawer = nodes.some(
        (node) => node instanceof Element && node.classList?.contains('cart-drawer')
      );
      if (!inDrawer) return;

      const isRemoveItem = nodes.some(
        (node) => node instanceof Element && node.classList?.contains('cart-items__remove')
      );
      const isRemoveDiscount = nodes.some(
        (node) => node instanceof Element && node.classList?.contains('cart-discount__pill-remove')
      );

      if (!isRemoveItem && !isRemoveDiscount) return;

      this.shimmerMessage();
      scheduleSharedCartRefreshes([200, 700, 1300]);
    };

    async refreshCartTotal() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) return;
        const cart = await response.json();
        if (cart?.total_price == null) return;
        if (!this.isConnected) return;

        const displayTotal = this.getDisplayCartTotalCents(cart);
        this.setCartTotal(displayTotal);
        this.update();
        relocateShippingRatesPopupShared();
      } catch (error) {
        // Keep the current progress if the cart endpoint is temporarily unavailable.
      }
    }

    /**
     * @deprecated Use relocateShippingRatesPopupShared — mantido como alias.
     */
    relocateShippingRatesPopup() {
      relocateShippingRatesPopupShared();
    }

    /**
     * Mesma lógica do cart-summary:
     * display_items (com PIX) - shopify_items + cart.total_price (+ SavePay cart-level)
     * Assim cupons Shopify entram, e o PIX de vitrine também — sem descontar SavePay 2x.
     */
    getPixCartLevelDiscountCents(cart) {
      let total = 0;
      const applications = Array.isArray(cart?.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];

      applications.forEach((discount) => {
        const title = String(discount?.title || '').toLowerCase();
        if (discount?.type === 'discount_code' && title.includes('pix')) {
          total += this.readNumber(discount.total_allocated_amount);
        }
      });

      return total;
    }

    getDisplayCartTotalCents(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      let displayItemsTotal = 0;
      let shopifyItemsTotal = 0;

      items.forEach((item) => {
        const finalLinePrice = this.readNumber(item.final_line_price);
        const originalLinePrice = this.readNumber(
          item.original_line_price,
          this.readNumber(item.original_price) * this.readNumber(item.quantity, 1)
        );
        shopifyItemsTotal += finalLinePrice;

        const row = document.querySelector(
          `.cart-drawer .cart-items__table-row[data-key="${item.key}"]`
        );
        const pixUnit = this.readNumber(row?.dataset?.pixUnitCents);
        const discountBaseUnit = this.readNumber(
          row?.dataset?.discountBaseUnitCents,
          this.readNumber(item.original_price)
        );

        if (originalLinePrice !== finalLinePrice) {
          displayItemsTotal += finalLinePrice;
        } else if (pixUnit > 0 && discountBaseUnit > pixUnit) {
          displayItemsTotal += pixUnit * this.readNumber(item.quantity, 1);
        } else if (row?.dataset?.displayLinePriceCents != null) {
          // Fallback pós-morph: preço de vitrine já renderizado na linha
          displayItemsTotal += this.readNumber(row.dataset.displayLinePriceCents, finalLinePrice);
        } else {
          displayItemsTotal += finalLinePrice;
        }
      });

      return (
        displayItemsTotal -
        shopifyItemsTotal +
        this.readNumber(cart.total_price) +
        this.getPixCartLevelDiscountCents(cart)
      );
    }

    update() {
      if (!this.isConnected) return;

      this.message = this.querySelector('[data-message]') || this.message;

      const savedAddress = this.getSavedAddress();
      const hasCep = this.hasSavedCep(savedAddress);
      const threshold = this.getActiveThreshold(savedAddress);
      const cartTotal = this.getReliableCartTotal();

      this.hidden = !hasCep;
      this.toggleAttribute('hidden', !hasCep);

      // Sem CEP: barra fica oculta (só o botão de verificar CEP no topo).
      if (!hasCep) {
        this.style.setProperty('--cart-free-shipping-progress', '0%');
        return;
      }

      const remaining = Math.max(threshold - cartTotal, 0);
      const progress = threshold > 0 ? Math.min((cartTotal / threshold) * 100, 100) : 100;

      this.style.setProperty('--cart-free-shipping-progress', `${progress}%`);

      if (!this.message) return;
      if (remaining <= 0) {
        this.setMessage(this.dataset.successText || 'Você já tem frete grátis!');
        return;
      }

      const before = this.dataset.textBeforeValue || 'Quase lá, adicione';
      const after = this.dataset.textAfterValue || 'mais para obter';
      const benefit = this.dataset.benefitLabel || 'FRETE GRÁTIS';
      this.setMessage(`${before} ${this.formatMoney(remaining)} ${after} ${benefit}`);
    }

    shimmerMessage() {
      this.message = this.querySelector('[data-message]') || this.message;
      this.message?.shimmer?.();
    }

    setMessage(text) {
      this.message = this.querySelector('[data-message]') || this.message;
      if (!this.message) return;
      this.message.textContent = text;
      this.message.setAttribute('value', text);
      this.message.removeAttribute('shimmer');
    }

    setCartTotal(total) {
      const cartTotal = this.readNumber(total);
      const hasVisibleItems = this.hasVisibleCartItems();
      const lastTotal = this.getStoredCartTotal();

      // Evita total 0 temporário enquanto ainda há itens (race do morph/otimismo).
      if (cartTotal <= 0 && hasVisibleItems && lastTotal > 0) {
        this.dataset.cartTotalCents = String(lastTotal);
        return;
      }

      this.dataset.cartTotalCents = String(cartTotal);
      if (cartTotal > 0 || !hasVisibleItems) {
        try {
          sessionStorage.setItem(this.storageKey, String(cartTotal));
        } catch (error) {
          // Ignore storage failures.
        }
      }
    }

    getReliableCartTotal() {
      const cartTotal = this.readNumber(this.dataset.cartTotalCents);
      const hasVisibleItems = this.hasVisibleCartItems();
      if (cartTotal > 0) return cartTotal;
      if (!hasVisibleItems) return cartTotal;
      return this.getStoredCartTotal();
    }

    hasVisibleCartItems() {
      return Boolean(
        document.querySelector(
          '.cart-drawer .cart-items__table-row[data-key]:not(.removing)'
        )
      );
    }

    getStoredCartTotal() {
      try {
        return this.readNumber(sessionStorage.getItem(this.storageKey));
      } catch (error) {
        return 0;
      }
    }

    getActiveThreshold(savedAddress = this.getSavedAddress()) {
      const region = this.getSavedRegion(savedAddress);
      const thresholds = {
        southeast: this.dataset.southeastThresholdCents,
        south: this.dataset.southThresholdCents,
        centerWest: this.dataset.centerWestThresholdCents,
        northeast: this.dataset.northeastThresholdCents,
        north: this.dataset.northThresholdCents,
      };

      if (region && thresholds[region] != null && thresholds[region] !== '') {
        return this.readNumber(thresholds[region]);
      }

      return this.readNumber(this.dataset.defaultThresholdCents);
    }

    getSavedRegion(savedAddress = this.getSavedAddress()) {
      const state = this.getSavedState(savedAddress);
      const regions = {
        southeast: ['SP', 'RJ', 'MG', 'ES'],
        south: ['PR', 'SC', 'RS'],
        centerWest: ['DF', 'GO', 'MT', 'MS'],
        northeast: ['BA', 'SE', 'AL', 'PE', 'PB', 'RN', 'CE', 'PI', 'MA'],
        north: ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO'],
      };

      return Object.keys(regions).find((region) => regions[region].includes(state));
    }

    hasSavedCep(savedAddress = this.getSavedAddress()) {
      const cep = String(savedAddress?.cep || savedAddress?.zip || savedAddress?.postalCode || '').replace(/\D/g, '');
      return cep.length === 8;
    }

    getSavedState(savedAddress = this.getSavedAddress()) {
      return String(savedAddress?.state || savedAddress?.uf || '').trim().toUpperCase();
    }

    getSavedAddress() {
      try {
        const fromLocal = this.readStorage(localStorage);
        const fromSession = this.readStorage(sessionStorage);

        if (!fromLocal) return fromSession;
        if (!fromSession) return fromLocal;

        const localUpdatedAt = Number(fromLocal.updatedAt) || 0;
        const sessionUpdatedAt = Number(fromSession.updatedAt) || 0;
        const newest = sessionUpdatedAt >= localUpdatedAt ? fromSession : fromLocal;
        this.persistAddress(newest);
        return newest;
      } catch (error) {
        return null;
      }
    }

    readStorage(storage) {
      try {
        const raw = storage.getItem(this.addressStorageKey);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    }

    persistAddress(addressData) {
      if (!addressData) return;

      const payload = {
        ...addressData,
        updatedAt: addressData.updatedAt || Date.now(),
      };
      const serialized = JSON.stringify(payload);

      try {
        if (localStorage.getItem(this.addressStorageKey) !== serialized) {
          localStorage.setItem(this.addressStorageKey, serialized);
        }
      } catch (error) {
        // Ignore quota / private mode failures.
      }

      try {
        if (sessionStorage.getItem(this.addressStorageKey) !== serialized) {
          sessionStorage.setItem(this.addressStorageKey, serialized);
        }
      } catch (error) {
        // Ignore quota / private mode failures.
      }

      return payload;
    }

    readNumber(value, fallback = 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(cents / 100);
    }
  }

  customElements.define('cart-free-shipping-progress', CartFreeShippingProgress);
}
